import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Box } from '@chakra-ui/react'

interface DeferredSectionProps {
  children: ReactNode
  /**
   * Reserved height shown before the section mounts, so filling it in does not
   * shift the page (CLS). Pick a value close to the section's rendered height.
   */
  minHeight: string | number
  /** How far outside the viewport to start mounting (preload before scroll-in). */
  rootMargin?: string
}

/**
 * Renders its children only once the placeholder approaches the viewport, then
 * keeps them mounted. Pairs with React.lazy so a heavy below-the-fold section's
 * code AND render are deferred until the user scrolls near it — improving the
 * landing LCP without a layout shift (the reserved minHeight holds the space).
 *
 * Without IntersectionObserver (e.g. jsdom, or very old browsers) it renders
 * immediately, so behavior degrades to eager rendering rather than blank.
 */
export const DeferredSection = ({
  children,
  minHeight,
  rootMargin = '400px',
}: DeferredSectionProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (visible) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const element = ref.current
    if (!element) return
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [visible, rootMargin])

  return (
    <Box ref={ref} minH={visible ? undefined : minHeight}>
      {visible ? children : null}
    </Box>
  )
}

export default DeferredSection
